import { useCallback, useEffect, useRef, useState } from 'react';

// UID จาก WebNFC มาเป็น "04:1a:2b:3c" — เก็บเป็น HEX ตัวใหญ่ไม่มีตัวคั่นให้ตรงกับฝั่งเซิร์ฟเวอร์
export const normalizeUid = (serial) =>
  String(serial || '').trim().toUpperCase().replace(/[\s:\-_.]/g, '');

// UID ตามมาตรฐาน ISO14443A ยาว 4, 7 หรือ 10 ไบต์
export const isValidUid = (uid) => /^[0-9A-F]+$/.test(uid) && [8, 14, 20].includes(uid.length);

// WebNFC มีเฉพาะ Chrome บน Android — เดสก์ท็อปไม่มีทางใช้ได้ไม่ว่าจะทำอะไร
// ต้องแยกออกมาก่อน ไม่งั้น PC ที่เปิดผ่าน http จะได้ข้อความ "ต้องเปิดผ่าน HTTPS"
// ซึ่งชวนให้เข้าใจผิดว่าแก้เป็น HTTPS แล้วจะใช้ได้
export const isMobileDevice = () =>
  navigator.userAgentData?.mobile ?? /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');

// คืน null = เครื่องนี้อ่านบัตรเองได้ / คืนสตริง = อ่านไม่ได้ พร้อมเหตุผลที่บอกทางแก้ได้จริง
export const nfcUnavailableReason = () => {
  if (!isMobileDevice()) return 'เครื่องนี้ไม่มี NFC ในตัว';
  // ลำดับนี้สลับไม่ได้: บน Android ที่เปิดผ่าน http ตัว NDEFReader จะไม่ถูกประกาศเลย
  // (เป็น SecureContext interface) ถ้าเช็คการรองรับก่อนจะสรุปผิดว่าเบราว์เซอร์ไม่รองรับ
  if (!window.isSecureContext) return 'ต้องเปิดหน้านี้ผ่าน HTTPS';
  if (!('NDEFReader' in window)) return 'เบราว์เซอร์นี้ไม่รองรับ NFC — ใช้ Chrome บน Android';
  return null;
};

/**
 * อ่านบัตร NFC จากมือถือ Android
 * onScan(uid, { valid }) ถูกเรียกเมื่ออ่านได้ — กัน bounce ให้แล้วโดยเว้น 1.5 วิ สำหรับ UID เดิม
 */
export default function useNfcReader(onScan, { repeatDelayMs = 1500 } = {}) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const lastRef = useRef({ uid: '', at: 0 });
  const onScanRef = useRef(onScan);
  const abortRef = useRef(null);
  // ต้องถือ NDEFReader ไว้ใน ref ไม่ใช่ตัวแปรในฟังก์ชัน — ถ้าปล่อยให้ถูก garbage collect
  // การสแกนจะหยุดเงียบๆ ทั้งที่ปุ่มยังขึ้นว่ากำลังสแกนอยู่
  const readerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  const attach = useCallback(async () => {
    // ปิดรอบก่อนหน้าเสมอ กันสแกนซ้อนกันหลายชุด
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (!readerRef.current) readerRef.current = new window.NDEFReader();
    const reader = readerRef.current;

    await reader.scan({ signal: controller.signal });   // ครั้งแรกต้องเรียกจากการกดปุ่มของผู้ใช้

    reader.onreading = (event) => {
      const uid = normalizeUid(event.serialNumber);
      const now = Date.now();
      if (uid === lastRef.current.uid && now - lastRef.current.at < repeatDelayMs) return;
      lastRef.current = { uid, at: now };
      onScanRef.current(uid, { valid: isValidUid(uid) });
    };

    reader.onreadingerror = () => {
      setError('อ่านบัตรไม่สำเร็จ — บัตรใบนี้อาจใช้กับมือถือไม่ได้ ลองใช้เครื่องอ่าน');
    };
  }, [repeatDelayMs]);

  const start = useCallback(async () => {
    const reason = nfcUnavailableReason();
    if (reason) { setError(reason); return false; }

    try {
      await attach();
      startedRef.current = true;
      setScanning(true);
      setError(null);
      return true;
    } catch (err) {
      setScanning(false);
      setError(
        err?.name === 'NotAllowedError'
          ? 'ไม่ได้รับอนุญาตให้ใช้ NFC — กดอนุญาตแล้วลองใหม่ หรือเปิด NFC ในตั้งค่าเครื่อง'
          : `เริ่มสแกนไม่ได้: ${err?.name || 'ไม่ทราบสาเหตุ'}`
      );
      return false;
    }
  }, [attach]);

  const stop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
    startedRef.current = false;
    setScanning(false);
  }, []);

  // Android ส่ง reading ให้เฉพาะหน้าที่กำลังมองเห็นอยู่ สลับแท็บหรือออกไปแอปอื่นแล้วการสแกนจะตาย
  // กลับมาเมื่อไหร่ต่อใหม่ให้เอง ไม่งั้นยามจะแตะบัตรแล้วจอเงียบโดยไม่รู้ว่าต้องกดปุ่มซ้ำ
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !startedRef.current) return;
      attach().catch(() => {
        setScanning(false);
        setError('การสแกนหยุดไปตอนสลับหน้าจอ — กดเริ่มสแกนอีกครั้ง');
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [attach]);

  // ออกจากหน้าแล้วต้องปิด ไม่งั้น NFC ค้างทำงานอยู่เบื้องหลัง
  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  return { scanning, error, start, stop, setError };
}
